// Stadium Trial — enemy catalogue (content only, no engine logic).
// Referenced by encounters via `enemyId`. Skills are declared for later; they
// are NOT implemented by the combat engine at this stage.

// Relative weight of each stat in the enemy's build. The combat engine scales
// these to the encounter's power budget (fixedPower / adaptive offset), so the
// TOTAL stays tuned by pveTiers while the SHAPE (aggressive / tank / …) comes
// from here. Weights need not sum to 1 — the engine normalises.
export interface PveStatWeights {
  physique: number;   // → damage per hit
  technique: number;  // → crit chance
  vitesse: number;    // → HP (vitesse × 10)
  defense: number;    // → incoming damage reduction
}

export interface PveEnemy {
  id: string;
  name: string;
  img?: string;              // artwork (fallback = test opponent for now)
  statWeights: PveStatWeights; // build shape (scaled to the encounter budget)
  tags?: string[];           // e.g. "aggressive", "tank", "final"
  weaknesses?: string[];     // e.g. "physique", "vitesse" — not consumed yet
  skills?: string[];         // declared only, no active skills yet
}

// Temporary placeholder artwork (mobs still use this until they get their own art).
const TEST_OPPONENT_IMG = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/player%20test.png";
// Boss artwork (shared across tiers until per-tier boss art is added).
const BOSS_T1_5_IMG = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/Boss-Tier1-5.png";
const BOSS_T1_10_IMG = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/Boss-Tier1-10.png";

export const PVE_ENEMIES: Record<string, PveEnemy> = {
  // ── Tier 1 mobs (adaptive) ────────────────────────────────────────────────
  t1_mob_striker:   { id: "t1_mob_striker",   name: "Rookie Striker", img: TEST_OPPONENT_IMG, tags: ["aggressive"], weaknesses: ["defense"],   statWeights: { physique: 0.40, technique: 0.25, vitesse: 0.20, defense: 0.15 } },
  t1_mob_runner:    { id: "t1_mob_runner",    name: "Winger",         img: TEST_OPPONENT_IMG, tags: ["fast"],       weaknesses: ["physique"],  statWeights: { physique: 0.25, technique: 0.30, vitesse: 0.30, defense: 0.15 } },
  t1_mob_wall:      { id: "t1_mob_wall",      name: "Defender",       img: TEST_OPPONENT_IMG, tags: ["tank"],       weaknesses: ["technique"], statWeights: { physique: 0.15, technique: 0.15, vitesse: 0.30, defense: 0.40 } },
  t1_mob_playmaker: { id: "t1_mob_playmaker", name: "Playmaker",      img: TEST_OPPONENT_IMG, tags: ["balanced"],   weaknesses: ["vitesse"],   statWeights: { physique: 0.25, technique: 0.25, vitesse: 0.25, defense: 0.25 } },

  // ── Tier 1 bosses (fixed power) ───────────────────────────────────────────
  t1_boss_captain:  { id: "t1_boss_captain",  name: "Rival Captain",  img: BOSS_T1_5_IMG,  tags: ["boss"],          weaknesses: ["vitesse"], statWeights: { physique: 0.30, technique: 0.25, vitesse: 0.25, defense: 0.20 } },
  t1_boss_ace:      { id: "t1_boss_ace",      name: "Stadium Ace",    img: BOSS_T1_10_IMG, tags: ["boss", "final"], weaknesses: ["defense"], statWeights: { physique: 0.28, technique: 0.28, vitesse: 0.24, defense: 0.20 } },
};

export function getEnemy(enemyId: string): PveEnemy | undefined {
  return PVE_ENEMIES[enemyId];
}

// ── Randomized per-run artwork ────────────────────────────────────────────────
// Uploaded to R2, all normalized to the player-character canvas size (drop-in for
// the 1v1 frame). The pools are picked PER RUN (seeded on the run id, so the
// selection survives a quit → resume mid-run and the two bosses never collide).
const CDN = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev";

export const PVE_BOSS_ARTS = [
  `${CDN}/Boss-Human.png`,
  `${CDN}/Boss-Human1.png`,
  `${CDN}/Boss-Human3.png`, // NB: no Boss-Human2 — numbering is intentionally sparse
  `${CDN}/Boss-Human4.png`,
  `${CDN}/Boss-Human5.png`,
  `${CDN}/Boss-Human6.png`,
  `${CDN}/Boss-Human7.png`,
];

export const PVE_SBIRE_ARTS = [
  `${CDN}/Sbire.png`,
  `${CDN}/Sbire2-Human.png`,
];

export const PVE_FAMILIAR_ARTS = [
  `${CDN}/Arena-familiar-Dobberman.png`,
  `${CDN}/Arena-familiar-Koi.png`,
  `${CDN}/Arena-familiar-RedPanda.png`,
  `${CDN}/Arena-familiar-Scorpio.png`,
  `${CDN}/Arena-familiar-Tiger.png`,
];

// FNV-1a over the run id, salted — deterministic and stable across resume.
function hashSeed(runId: string | null | undefined, salt: number): number {
  let h = (2166136261 ^ salt) >>> 0;
  const s = runId ?? "";
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * Enemy artwork for one encounter, randomized PER RUN (seed = run id):
 *  - boss → one of PVE_BOSS_ARTS; a run's two bosses (stage 5 & 10) are
 *           GUARANTEED different (distinct-index pick off the same seed).
 *  - mob  → one of PVE_SBIRE_ARTS (repeats across rooms are fine, by design).
 * Names/stats are untouched — this only swaps the sprite.
 */
export function pickEnemyArt(runId: string | null | undefined, stage: number, kind: "mob" | "boss"): string {
  if (kind === "boss") {
    const n = PVE_BOSS_ARTS.length;
    const first = hashSeed(runId, 0x05) % n;              // the run's stage-5 boss
    const step = 1 + (hashSeed(runId, 0x0a) % (n - 1));   // 1..n-1 → the offset is never 0
    const idx = stage <= 5 ? first : (first + step) % n;  // stage-10 boss ≠ stage-5 boss
    return PVE_BOSS_ARTS[idx];
  }
  return PVE_SBIRE_ARTS[hashSeed(runId, stage) % PVE_SBIRE_ARTS.length];
}

/** Enemy companion (2v2) artwork, randomized per room off the same run seed. */
export function pickFamiliarArt(runId: string | null | undefined, stage: number): string {
  return PVE_FAMILIAR_ARTS[hashSeed(runId, stage + 100) % PVE_FAMILIAR_ARTS.length];
}
